import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { useSmartContent } from '../context/SmartSiteContext';
import axios from 'axios';
import { Toaster, toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const contactData = useSmartContent('contact-cta', 'cta');
  const c = contactData.items?.[0]?.data || {};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/public/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const inputCls = "w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4] text-gray-900 placeholder:text-gray-400";

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <Toaster position="top-right" richColors />
      {/* Hero */}
      <section className="relative py-24 bg-[#1B3A5C] overflow-hidden">
        <div className="absolute inset-0 opacity-5 pattern-dots" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <nav className="text-sm text-blue-200/50 mb-4">
            <Link to="/" className="hover:text-[#F5A623]">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-white">Contact</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4 text-white">{c.heading || 'Contact Us'}</h1>
          <p className="text-blue-100/70 max-w-2xl">
            {c.body || "If you don't see what you're looking for, feel free to be in touch!"}
          </p>
          {c.cta_text && <Link to={c.cta_link || "/quote/builder"} className="inline-block mt-5 px-5 py-3 bg-[#F5A623] text-gray-900 rounded-full">{c.cta_text}</Link>}
          {c.image && <img src={c.image} alt="" className="mt-6 max-h-64 max-w-full object-contain rounded-lg"/>}
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-2xl font-bold mb-8 text-gray-900">Contact Info</h2>
              <div className="space-y-6 mb-12">
                {[
                  { icon: MapPin, title: 'Address', content: '129 Slade Ave, Pikesville, MD 21208' },
                  { icon: Phone, title: 'Phone', content: '443-940-6660', href: 'tel:443-940-6660' },
                  { icon: Mail, title: 'Email', content: 'info@shopkyis.com', href: 'mailto:info@shopkyis.com' },
                  { icon: Clock, title: 'Hours', content: 'Mon - Fri: 10:00 AM - 6:00 PM' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="p-3 bg-[#4A90A4]/10 rounded-xl">
                      <item.icon className="w-6 h-6 text-[#4A90A4]" />
                    </div>
                    <div>
                      <h3 className="font-bold mb-1 text-gray-900">{item.title}</h3>
                      {item.href ? (
                        <a href={item.href} className="text-gray-500 hover:text-[#4A90A4] transition-colors">{item.content}</a>
                      ) : (
                        <p className="text-gray-500">{item.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Let us Kre8 your idea!</h3>
              <p className="text-gray-500 mb-6">Feel free to reach out via social media as well.</p>
              <div className="flex gap-3">
                <a href="https://web.facebook.com/kre8ingyourideas" target="_blank" rel="noopener noreferrer"
                  className="px-5 py-2.5 border border-gray-200 rounded-full text-sm font-medium text-gray-600 hover:border-[#4A90A4] hover:text-[#4A90A4] transition-all">
                  Facebook
                </a>
                <a href="https://www.instagram.com/kre8ing_your_ideas/" target="_blank" rel="noopener noreferrer"
                  className="px-5 py-2.5 border border-gray-200 rounded-full text-sm font-medium text-gray-600 hover:border-[#4A90A4] hover:text-[#4A90A4] transition-all">
                  Instagram
                </a>
              </div>
            </div>

            <div className="p-8 bg-[#FDF6E9] rounded-2xl">
              <h2 className="text-2xl font-bold mb-6 text-gray-900">Send us a message</h2>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Name *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required className={inputCls} data-testid="contact-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Email *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputCls} data-testid="contact-email" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Phone</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={inputCls} data-testid="contact-phone" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">Subject</label>
                    <input type="text" name="subject" value={formData.subject} onChange={handleChange} className={inputCls} data-testid="contact-subject" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">Message *</label>
                  <textarea name="message" value={formData.message} onChange={handleChange} required rows={5}
                    className={`${inputCls} resize-none`} data-testid="contact-message" />
                </div>
                <button type="submit" disabled={loading}
                  className="w-full flex items-center justify-center px-8 py-4 bg-[#F5A623] text-gray-900 font-bold rounded-full btn-glow disabled:opacity-50"
                  data-testid="contact-submit"
                >
                  {loading ? 'Sending...' : 'Send Message'} <Send className="ml-2 w-5 h-5" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
